import axios from "axios";
import { loginState } from "../states.ts";
import { TodoItem, TodoList, TodoListGroup, TodoListShare } from "./types";

export const API_URL = import.meta.env.VITE_API_URL;

export interface ApiResponse {
	ok: boolean;
}

export interface ListsResponse extends ApiResponse {
	lists: TodoList[];
}

export const fetchLists = async () => {
	return get<ListsResponse>(`/api/lists`);
};

export const fetchList = async (listId: string) => {
	return get<ListResponse>(`/api/lists/${listId}`);
};

export interface ItemsResponse extends ApiResponse {
	items: TodoItem[];
}

export const fetchItems = async (listId: string) => {
	return get<ItemsResponse>(`/api/lists/${listId}/items`);
};

export interface ListResponse extends ApiResponse {
	list: TodoList;
}

export const apiCreateList = async (list: TodoList) => {
	return post<ListResponse>(`/api/lists`, list);
};

export interface GroupsResponse extends ApiResponse {
	groups: TodoListGroup[];
}

export interface GroupResponse extends ApiResponse {
	group: TodoListGroup;
}

export const fetchGroups = async () => {
	return get<GroupsResponse>(`/api/groups`);
};

export const apiCreateGroup = async (group: TodoListGroup) => {
	return post<GroupResponse>(`/api/groups`, group);
};

export const apiUpdateGroup = async (group: TodoListGroup) => {
	return put<GroupResponse>(`/api/groups/${group.id}`, group);
};

export const apiDeleteGroup = async (groupId: string) => {
	return remove<ApiResponse>(`/api/groups/${groupId}`);
};

export interface ItemResponse extends ApiResponse {
	item: TodoItem;
}

export const apiCreateItem = async (listId: string, item: TodoItem) => {
	return post<ItemResponse>(`/api/lists/${listId}/items`, item);
};

export const apiDeleteList = async (listId: string) => {
	return remove<ApiResponse>(`/api/lists/${listId}`);
};

export const apiUpdateList = async (list: TodoList) => {
	return put<ListResponse>(`/api/lists/${list.id}`, list);
};

export const apiDeleteItems = async (listId: string, itemIds: string[]) => {
	return remove<ApiResponse>(`/api/lists/${listId}/items`, itemIds);
};

export const apiUpdateItem = async (item: TodoItem) => {
	return put<ItemResponse>(`/api/items/${item.id}`, item);
};

export interface SortEntry {
	id: string;
	order: number;
	group_id?: string | null;
}

export const apiSetListsSort = async (sorts: SortEntry[]) => {
	return post<ListResponse>(`/api/lists/sort`, sorts);
};

export const apiSetListItemsSort = async (listId: string, sorts: SortEntry[]) => {
	return post<ListResponse>(`/api/lists/${listId}/sort`, sorts);
};

export interface SharesResponse extends ApiResponse {
	shares: TodoListShare[];
}

export const fetchShares = async (listId: string) => {
	return get<SharesResponse>(`/api/lists/${listId}/shares`);
};

export interface ShareResponse extends ApiResponse {
	share: TodoListShare;
}

export const apiCreateShare = async (
	listId: string,
	email: string,
	permission: "read" | "write" = "read",
) => {
	return post<ShareResponse>(`/api/lists/${listId}/shares`, { email, permission });
};

export const apiCreateExternalShare = async (
	listId: string,
	permission: "read" | "write" = "read",
) => {
	return post<ShareResponse>(`/api/lists/${listId}/shares`, { external: true, permission });
};

export const apiDeleteShare = async (listId: string, shareId: string) => {
	return remove<ApiResponse>(`/api/lists/${listId}/shares/${shareId}`);
};

export interface VoiceResponse extends ApiResponse {
	job_id: string;
}

export interface VoiceJobStatus extends ApiResponse {
	status: "queued" | "transcribing" | "parsing" | "done" | "error";
	stage?: string;
	error?: string;
	result?: {
		transcript: string;
		items: TodoItem[];
		lists: TodoList[];
	};
}

export const apiVoiceAdd = async (audioBase64: string) => {
	return post<VoiceResponse>(`/api/voice`, { audio: audioBase64 });
};

export const fetchVoiceStatus = async (jobId: string) => {
	return get<VoiceJobStatus>(`/api/voice/${jobId}`);
};

const request = async <R>(method: "get" | "post" | "put" | "delete", url: string, data?: any) => {
	const headers: Record<string, string> = {
		Authorization: loginState.token ?? "",
		...(data !== undefined ? { "Content-Type": "application/json" } : {}),
		...(loginState.shareToken ? { "X-Share-Token": loginState.shareToken } : {}),
	};

	try {
		const result = await axios.request<R>({
			method,
			url: `${API_URL}${url}`,
			data,
			headers,
		});
		return result.data;
	} catch (e: any) {
		if (e.status === 403 && e.response?.data?.error === "Authentication required") {
			loginState.token = undefined;
		}
		throw e;
	}
};

const get = async <R>(url: string, query?: Record<string, any>) => {
	const queryString = query ? "?" + new URLSearchParams(query).toString() : "";
	return request<R>("get", url + queryString);
};

const post = <R>(url: string, data: any) => request<R>("post", url, data);

const remove = <R>(url: string, data?: any) => request<R>("delete", url, data);

const put = <R>(url: string, data: any) => request<R>("put", url, data);
